import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Check, X, Pencil } from "lucide-react";

export default function Catalogue() {
  const [pieces, setPieces] = useState([]);
  const [selected, setSelected] = useState("");
  const [elements, setElements] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [editing, setEditing] = useState(null);

  const loadPieces = async () => {
    const { data } = await api.get("/catalogue/pieces");
    setPieces(data);
    if (!selected && data.length) setSelected(data[0].code);
  };
  const loadElements = async (code) => {
    if (!code) return;
    const { data } = await api.get("/catalogue/elements", { params: { type_piece: code } });
    setElements(data);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPieces(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadElements(selected); }, [selected]);

  const addElement = async () => {
    if (!newLabel.trim()) return;
    try {
      await api.post("/catalogue/elements", { type_piece: selected, libelle: newLabel.trim(), ordre: elements.length + 1 });
      setNewLabel("");
      loadElements(selected);
      toast.success("Élément ajouté");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveEdit = async (el) => {
    await api.put(`/catalogue/elements/${el.id}`, { type_piece: el.type_piece, libelle: editing.value, ordre: el.ordre });
    setEditing(null);
    loadElements(selected);
  };

  const del = async (id) => {
    await api.delete(`/catalogue/elements/${id}`);
    loadElements(selected);
  };

  return (
    <div>
      <h1 className="text-3xl font-bold text-dd-blue mb-1">Catalogue d'éléments</h1>
      <p className="text-sm text-dd-warm mb-6">Postes à contrôler par type de pièce. Modifie le catalogue vivant ; les constats déjà créés ne changent pas.</p>

      <div className="flex flex-col lg:flex-row gap-6">
        <aside className="lg:w-64 shrink-0 bg-white rounded-lg border border-dd-light p-2 h-fit">
          {pieces.map((p) => (
            <button key={p.id} data-testid={`cat-piece-${p.code}`} onClick={() => setSelected(p.code)}
              className={`w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors ${
                selected === p.code ? "bg-dd-blue text-white" : "text-dd-night hover:bg-dd-light/60"
              }`}>{p.libelle}</button>
          ))}
        </aside>

        <div className="flex-1 bg-white rounded-lg border border-dd-light p-5">
          <div className="flex gap-2 mb-4">
            <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Nouveau poste…"
              className="h-11" data-testid="new-element-input" onKeyDown={(e) => e.key === "Enter" && addElement()} />
            <Button onClick={addElement} data-testid="add-element-btn" className="h-11 bg-dd-blue hover:bg-dd-night text-white shrink-0">
              <Plus size={16} className="mr-1" /> Ajouter
            </Button>
          </div>
          <div className="divide-y divide-dd-light">
            {elements.map((el) => (
              <div key={el.id} className="flex items-center gap-3 py-2.5" data-testid={`element-${el.id}`}>
                {editing?.id === el.id ? (
                  <>
                    <Input value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} className="h-10 flex-1" />
                    <button onClick={() => saveEdit(el)} className="text-dd-blue"><Check size={18} /></button>
                    <button onClick={() => setEditing(null)} className="text-dd-warm"><X size={18} /></button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm text-dd-night">{el.libelle}</span>
                    <button onClick={() => setEditing({ id: el.id, value: el.libelle })} className="text-dd-blue"><Pencil size={16} /></button>
                    <button onClick={() => del(el.id)} className="text-destructive"><Trash2 size={16} /></button>
                  </>
                )}
              </div>
            ))}
            {elements.length === 0 && <p className="text-sm text-dd-warm py-4">Aucun élément pour ce type.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
